import { EVENTS_TYPES_CONTENT, REPLACE_KEYS } from 'constants/tool-history.constants';
import { IToolHistoryRelevantInfo } from 'types/toolHistory.types';
import { formatShortDateAndHour } from './formatDatesAndHours';

/**
 * Formatea una fecha a DD/MM/YYYY - HH:mm am/pm
 * @param date - Fecha a formatear
 * @returns Fecha formateada o null
 */
export const resolverDate = (date?: Date | string | null): string | null => {
  if (!date) return null;
  
  const isoString = date instanceof Date ? date.toISOString() : date;
  return formatShortDateAndHour(isoString, -6)?.onlyDateAndHour || null;
};

/** Mapeo de placeholders a sus valores en relevantInfo */
type RelevantInfoResolver = (info?: IToolHistoryRelevantInfo) => string;

const PLACEHOLDER_RESOLVERS: Record<string, RelevantInfoResolver> = {
  [REPLACE_KEYS.EVENT_CREATED_AT]: info => resolverDate(info?.createdAt) || 'Fecha no identificada',
  [REPLACE_KEYS.RESPONSIBLE_USERNAME]: info => info?.responsibleUserName || 'Usuario no identificado',
  [REPLACE_KEYS.TOOL_LOCATION_SOURCE]: info => info?.locationOriginName || 'Origen no identificado',
  [REPLACE_KEYS.TOOL_LOCATION_DESTINATION]: info => info?.locationDestinationName || 'Destino no identificado',
  [REPLACE_KEYS.CONFIGURATION_NAME]: info => info?.configurationName || 'Configuración no identificada',
  [REPLACE_KEYS.ARRIVAL_DATE]: info => resolverDate(info?.arrivalDate) || 'Fecha de llegada no identificada',
  [REPLACE_KEYS.DEPARTURE_DATE]: info => resolverDate(info?.departureDate) || 'Fecha de salida no identificada',
  [REPLACE_KEYS.NEW_TOOL_CONDITION]: info => info?.condition || 'Condición no identificada',
  [REPLACE_KEYS.NEW_TOOL_STATUS]: info => info?.status || 'Estado no identificado',
  [REPLACE_KEYS.OLD_TOOL_SERIAL]: info => info?.previousValue || 'N/A',
  [REPLACE_KEYS.NEW_TOOL_SERIAL]: info => info?.newValue || 'N/A',
  [REPLACE_KEYS.OLD_TOOL_SKU]: info => info?.previousValue || 'N/A',
  [REPLACE_KEYS.NEW_TOOL_SKU]: info => info?.newValue || 'N/A'
};

/**
 * Reemplaza placeholders en un texto con valores de relevantInfo
 * @param text - Texto con placeholders
 * @param relevantInfo - Datos del evento
 * @returns Texto procesado y capitalizado
 */
export const replaceEventPlaceholders = (text?: string | null, relevantInfo?: IToolHistoryRelevantInfo): string => {
  if (!text) return '';

  let result = text;
  for (const [placeholder, resolver] of Object.entries(PLACEHOLDER_RESOLVERS)) {
    if (!result.includes(placeholder)) continue;
    result = result.replace(placeholder, resolver(relevantInfo));
  }

  return result.charAt(0).toUpperCase() + result.slice(1);
};


interface IEventContent {
  title: string | null;
  description: string | null;
}

/**
 * Obtiene title y description base de un evento por UUID
 * @param eventUuid - UUID del evento
 * @returns Objeto con title y description
 */
export const getEventContentByUuid = (eventUuid?: string | null): IEventContent => {
  const initialValue = { title: null, description: null };
  if (!eventUuid) return initialValue;

  const event = EVENTS_TYPES_CONTENT[eventUuid as keyof typeof EVENTS_TYPES_CONTENT];
  if (!event) return initialValue;

  return { title: event.title || null, description: event.subtitle || null };
};


/**
 * Obtiene el contenido de un evento con placeholders reemplazados
 * @param eventUuid - UUID del evento
 * @param relevantInfo - Datos del evento
 * @returns Objeto con title y description procesados
 */
export const getProcessedEventContent = (
  eventUuid?: string | null,
  relevantInfo?: IToolHistoryRelevantInfo
): IEventContent => {
  const baseContent = getEventContentByUuid(eventUuid);

  return {
    title: replaceEventPlaceholders(baseContent.title, relevantInfo),
    description: replaceEventPlaceholders(baseContent.description, relevantInfo)
  };
};
